/**
 * Hand-written types for `GET /api/admin/users/:id/memberships` (ADR-0017 A8,
 * API-MEMB-1). Mirrors `src/organizations/models/admin-user-memberships.model.ts`
 * on the API; see CLAUDE.md -> TD-07 for why the UI does not import the
 * generated surface. All fields are `readonly`.
 */

/** Mirrors the API's `MembershipRole` enum (ADR-0017 section 1). */
export const MembershipRole = {
  OWNER: "OWNER",
  ADMIN: "ADMIN",
  STAFF: "STAFF",
} as const;

export type MembershipRole = (typeof MembershipRole)[keyof typeof MembershipRole];

export type MembershipStatus = "ACTIVE" | "INACTIVE";

/** How the membership came to exist. */
export type MembershipSource = "BACKFILL" | "INVITATION" | "ADMIN" | "OWNER";

export type AccountType = "GUEST" | "BUSINESS" | "INTERNAL";

/** Organization / location kind as the API spells it. */
export type OrganizationKind = "VENUE" | "PROPERTY";

export interface MembershipOrganization {
  readonly id: string;
  readonly name: string;
  readonly kind: OrganizationKind;
  /** ADR-0009 partition flag: staff see both partitions. */
  readonly isTest: boolean;
  /** `false` once the organization was deactivated (its owner left). */
  readonly active: boolean;
}

/** A STAFF assignment: venue / property id and name, `active` = the assignment's flag. */
export interface MembershipLocation {
  readonly kind: OrganizationKind;
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
}

export interface AdminMembership {
  readonly id: string;
  readonly organization: MembershipOrganization;
  readonly role: MembershipRole;
  readonly status: MembershipStatus;
  readonly source: MembershipSource;
  readonly createdAt: string;
  readonly deactivatedAt: string | null;
  /** The role's permission strings (the guard's policy table), sorted. */
  readonly permissions: readonly string[];
  /** STAFF only; `[]` for OWNER and ADMIN. */
  readonly locations: readonly MembershipLocation[];
}

export interface AdminUserMemberships {
  readonly userId: string;
  readonly accountType: AccountType;
  readonly memberships: readonly AdminMembership[];
}
